<script setup>
import { computed } from 'vue';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  deal: {
    type: Object,
    required: true,
  },
});

defineEmits(['click']);

const formatCurrency = cents => {
  if (!cents) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
};

const priorityDotClass = priority => {
  const colors = {
    urgent: 'bg-n-ruby-9',
    high: 'bg-n-amber-9',
    medium: 'bg-n-blue-9',
    low: 'bg-n-teal-9',
  };
  return colors[priority] || 'bg-n-slate-9';
};

const priorityAccentClass = priority => {
  const colors = {
    urgent: 'border-l-n-ruby-9',
    high: 'border-l-n-amber-9',
    medium: 'border-l-n-blue-9',
    low: 'border-l-n-teal-9',
  };
  return colors[priority] || 'border-l-n-slate-6';
};

const hasAmount = cents => Number(cents) > 0;

const contactInitial = contact => contact?.name?.[0]?.toUpperCase() || '?';

const assigneeInitial = assignee => assignee?.name?.[0]?.toUpperCase() || '?';

const conversation = computed(() => props.deal.conversations?.[0] || null);
const customAttributeCount = computed(
  () => Object.keys(props.deal.custom_attributes || {}).length
);

const getInboxIcon = channelType => {
  const icons = {
    'Channel::Whatsapp': 'i-lucide-phone',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::TwitterOrganisational': 'i-lucide-twitter',
  };
  return icons[channelType] || 'i-lucide-message-square';
};

const waitingTime = computed(() => {
  if (!conversation.value?.waiting_since) return '';
  return shortTimestamp(dynamicTime(conversation.value.waiting_since));
});

const expectedCloseLabel = computed(() => {
  if (!props.deal.expected_close_date) return '';
  return props.deal.expected_close_date;
});
</script>

<template>
  <div
    class="group cursor-grab rounded-lg border border-l-4 border-n-weak bg-n-solid-1 p-3 shadow-sm transition-all duration-200 hover:border-n-strong hover:bg-n-solid-2 active:cursor-grabbing hover:shadow-md"
    :class="priorityAccentClass(deal.priority)"
    draggable="true"
    @click="$emit('click', deal)"
    @dragstart="$event.dataTransfer.setData('deal-id', deal.id.toString())"
  >
    <!-- Priority and Title -->
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="mb-1 flex min-w-0 flex-wrap items-center gap-1.5">
          <span
            v-if="deal.priority && deal.priority !== 'no_priority'"
            class="inline-flex items-center gap-1 rounded-md border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full"
              :class="priorityDotClass(deal.priority)"
            />
            {{ deal.priority }}
          </span>
          <span
            v-if="conversation?.status"
            class="rounded-md border border-n-weak bg-n-alpha-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-n-slate-11"
          >
            {{ conversation.status }}
          </span>
        </div>
        <span class="line-clamp-2 text-sm font-semibold text-n-slate-12">
          {{ deal.name }}
        </span>
      </div>
      <span
        v-if="conversation?.sla_policy_id && conversation?.waiting_since"
        class="i-lucide-triangle-alert mt-1 size-4 flex-shrink-0 text-n-amber-10"
        :title="$t('KANBAN.DEAL.SLA_ACTIVE_TOOLTIP')"
      />
    </div>

    <!-- Contact Info -->
    <div v-if="deal.contact" class="mt-2.5 flex items-center gap-2">
      <div class="flex min-w-0 items-center gap-2">
        <img
          v-if="deal.contact.thumbnail"
          :src="deal.contact.thumbnail"
          class="size-7 flex-shrink-0 rounded-md object-cover border border-n-weak"
          alt=""
        />
        <div
          v-else
          class="flex size-7 flex-shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-xs font-semibold text-n-slate-12"
        >
          {{ contactInitial(deal.contact) }}
        </div>
        <div class="min-w-0">
          <span class="block truncate text-xs font-medium text-n-slate-12">
            {{ deal.contact.name }}
          </span>
          <span
            v-if="deal.contact.email || deal.contact.phone_number"
            class="block truncate text-[11px] text-n-slate-11"
          >
            {{ deal.contact.email || deal.contact.phone_number }}
          </span>
        </div>
      </div>
    </div>

    <!-- Conversation metadata (Inbox, Waiting, SLA, Link) -->
    <div
      v-if="conversation"
      class="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-dashed border-n-weak pt-2"
    >
      <!-- Inbox badge -->
      <span
        v-if="conversation.inbox_name"
        class="inline-flex items-center gap-1 rounded bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-medium text-n-slate-11 border border-n-weak/30"
        :title="conversation.inbox_name"
      >
        <span
          class="size-3 text-n-slate-10"
          :class="getInboxIcon(conversation.channel_type)"
        />
        <span class="max-w-[80px] truncate">{{ conversation.inbox_name }}</span>
      </span>

      <!-- SLA badge -->
      <span
        v-if="conversation.sla_policy_id"
        class="inline-flex items-center gap-1 rounded bg-r-500/10 border border-r-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-r-600 dark:text-r-400"
        :title="$t('KANBAN.DEAL.SLA_ACTIVE_TOOLTIP')"
      >
        <span class="i-lucide-alarm-clock size-3" />
        <span>{{ $t('KANBAN.DEAL.SLA_ACTIVE') }}</span>
      </span>

      <!-- Waiting Time -->
      <span
        v-if="waitingTime"
        class="inline-flex items-center gap-1 rounded bg-y-500/10 border border-y-500/20 px-1.5 py-0.5 text-[10px] font-medium text-y-600 dark:text-y-400"
        :title="$t('KANBAN.DEAL.WAITING_TIME_TOOLTIP')"
      >
        <span class="i-lucide-clock size-3" />
        <span>{{ waitingTime }}</span>
      </span>

      <!-- Conversation Link -->
      <router-link
        :to="{
          name: 'inbox_conversation',
          params: {
            conversation_id: conversation.display_id || conversation.id,
          },
        }"
        class="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-n-brand hover:underline"
        @click.stop
      >
        <span class="i-lucide-external-link size-3" />
        {{ $t('KANBAN.DEAL.HASH_SYMBOL')
        }}{{ conversation.display_id || conversation.id }}
      </router-link>
    </div>

    <!-- Labels -->
    <div v-if="conversation?.labels?.length" class="mt-2 flex flex-wrap gap-1">
      <span
        v-for="label in conversation.labels"
        :key="label"
        class="rounded bg-n-alpha-2 border border-n-weak/20 px-1.5 py-0.5 text-[10px] font-medium text-n-slate-11"
      >
        {{ label }}
      </span>
    </div>

    <!-- Footer: Value and Assignee -->
    <div
      class="mt-3 flex items-center justify-between gap-2 border-t border-n-weak pt-2 text-xs text-n-slate-11"
    >
      <span
        v-if="hasAmount(deal.amount_cents)"
        class="flex-shrink-0 rounded-md bg-n-teal-9/10 px-2 py-1 font-semibold text-n-teal-11"
      >
        {{ formatCurrency(deal.amount_cents) }}
      </span>
      <span v-else class="text-n-slate-10 text-[10px]">
        {{ $t('KANBAN.DEAL.NO_VALUE') }}
      </span>

      <div v-if="deal.assignee" class="flex min-w-0 items-center gap-1.5">
        <span class="truncate text-[11px]">{{ deal.assignee.name }}</span>
        <img
          v-if="deal.assignee.thumbnail"
          :src="deal.assignee.thumbnail"
          class="size-6 flex-shrink-0 rounded-full object-cover border border-n-weak"
          :title="deal.assignee.name"
          alt=""
        />
        <div
          v-else
          class="flex size-6 flex-shrink-0 items-center justify-center rounded-full bg-n-brand/20 text-[10px] font-semibold text-n-brand"
          :title="deal.assignee.name"
        >
          {{ assigneeInitial(deal.assignee) }}
        </div>
      </div>
      <div v-else class="text-[11px] text-n-slate-10 italic">
        {{ $t('KANBAN.DEAL.UNASSIGNED') }}
      </div>
    </div>

    <div
      class="mt-2 grid grid-cols-2 gap-2 border-t border-n-weak pt-2 text-[11px] text-n-slate-10"
    >
      <span class="inline-flex min-w-0 items-center gap-1">
        <span class="i-lucide-calendar size-3" />
        <span class="truncate">
          {{ expectedCloseLabel || $t('KANBAN.DEAL.NO_CLOSE_DATE') }}
        </span>
      </span>
      <span class="inline-flex min-w-0 items-center gap-1 justify-end">
        <span class="i-lucide-sliders-horizontal size-3" />
        <span class="truncate">
          {{
            $t('KANBAN.DEAL.CUSTOM_FIELDS_COUNT', {
              count: customAttributeCount,
            })
          }}
        </span>
      </span>
    </div>
  </div>
</template>
